"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

export function SiteHeader() {
  const pathname = usePathname() || "/";

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        background: "var(--paper)",
        borderBottom: "1px solid rgba(0,0,0,0.10)",
        backdropFilter: "saturate(180%) blur(6px)",
      }}
    >
      <div
        style={{
          maxWidth: 1024,
          margin: "0 auto",
          padding: "14px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <Link
          href="/"
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <span style={{ fontWeight: 900, letterSpacing: "0.2px" }}>
            Verification Press
          </span>
          <span
            style={{
              fontSize: 12,
              opacity: 0.72,
              border: "1px solid rgba(0,0,0,0.12)",
              padding: "3px 8px",
              borderRadius: 999,
              whiteSpace: "nowrap",
            }}
          >
            Evidence · Method · Clarity
          </span>
        </Link>

        <nav style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <NavLink pathname={pathname} href="/canon" label="Canon" />
          <NavLink pathname={pathname} href="/method" label="Method" />
          <NavLink pathname={pathname} href="/about" label="About" />
          <NavLink pathname={pathname} href="/contact" label="Contact" cta />
        </nav>
      </div>
    </header>
  );
}

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  // Active if exact match OR inside section (e.g. /canon/*)
  return pathname === href || pathname.startsWith(href + "/");
}

function NavLink({
  pathname,
  href,
  label,
  cta,
}: {
  pathname: string;
  href: string;
  label: string;
  cta?: boolean;
}) {
  const active = isActive(pathname, href);

  return (
    <>
      <Link
        href={href}
        aria-current={active ? "page" : undefined}
        className={`vp-nav-link ${active ? "active" : ""} ${
          cta ? "cta" : ""
        }`}
        title={label}
      >
        {label}
      </Link>

      <style jsx>{`
        .vp-nav-link {
          text-decoration: none;
          font-size: 14px;
          padding: 8px 12px;
          border-radius: 999px;
          font-weight: ${cta ? 800 : 700};
          opacity: 0.92;
          display: inline-flex;
          align-items: center;
          border: 1px solid transparent;
          background: transparent;
          color: inherit;
          transition: background 140ms ease,
                      border 140ms ease,
                      transform 100ms ease,
                      box-shadow 140ms ease;
        }

        /* CTA base */
        .vp-nav-link.cta {
          border: 1px solid rgba(0, 0, 0, 0.16);
          background: rgba(0, 0, 0, 0.04);
        }

        /* Hover (non-active only) */
        .vp-nav-link:hover:not(.active) {
          border: 1px solid rgba(0, 0, 0, 0.12);
          background: rgba(0, 0, 0, 0.05);
          transform: translateY(-1px);
        }

        /* Active = Brand pill */
        .vp-nav-link.active {
          border: 1px solid rgba(0, 0, 0, 0.18);
          background: color-mix(
            in oklab,
            var(--brand) 14%,
            transparent
          );
          box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
        }

        /* Active CTA variant */
        .vp-nav-link.cta.active {
          border: 1px solid rgba(0, 0, 0, 0.22);
          background: color-mix(
            in oklab,
            var(--brand) 12%,
            rgba(0, 0, 0, 0.04)
          );
        }
      `}</style>
    </>
  );
}